import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * core
 */
import { ComparisonOperator, ComparisonOperatorType, IComparisonOperator } from '@core/enums/comparison-operator';

/**
 * enums
 */
import { CompareErrorTarget, ICompareErrorTarget } from './enums';

/**
 * interfaces
 */
import { IDateCompareOptions } from './interfaces';

/**
 * consts
 */
import { COMPARE_I18N_KEYS } from './consts';

/**
 * models
 */
import { AppValidationErrorsModel } from '../models';

/**
 * Date-aware validators — the home for every rule that compares or constrains date, time and
 * datetime controls, so feature modules stop re-implementing the same start/end check.
 *
 * All methods are static — no instantiation needed.
 *
 * Currently exposed:
 * - {@link DateValidator.compare} — group-level rule between two sibling controls
 *
 * Every validator here treats an empty side as **valid** (returns `null`), so it composes
 * cleanly with `AppValidators.required` rather than duplicating it.
 *
 * @abstract
 * @example
 * ```typescript
 * // end date may equal start date, but never precede it
 * this._fb.group({
 *   start_date: this._fb.control<string | null>(null),
 *   end_date:   this._fb.control<string | null>(null),
 * }, {
 *   validators: [DateValidator.compare({ startKey: 'start_date', endKey: 'end_date' })],
 * });
 *
 * // same rule, but the message shows under the end field instead of on the group
 * DateValidator.compare({
 *   startKey: 'start_date',
 *   endKey: 'end_date',
 *   target: CompareErrorTarget.End,
 * });
 *
 * // strictly-before rule with a module-specific message
 * DateValidator.compare({
 *   startKey: 'from',
 *   endKey: 'to',
 *   operator: ComparisonOperator.LessThan,
 *   i18nKey: 'SCHEDULE.FORM.VALIDATION.END_BEFORE_START',
 * });
 * ```
 */
export abstract class DateValidator {

    // ─── public validators ───────────────────────────────────────────────────

    /**
     * Group-level validator: the rule `start <operator> end` must hold between two sibling
     * controls of the group this validator is attached to.
     *
     * The operator is read **start-first** — `LessThan` means *start must be before end* — and
     * defaults to {@link ComparisonOperator.LessThanOrEqual}, the common "end may equal start,
     * but never precede it" range rule.
     *
     * Values may be `Date`s, timestamps, or strings; see {@link DateValidator._timestamp} for how
     * mixed inputs are resolved. Void (`null`) whenever either side is empty, disabled, or the
     * two values are not comparable at all — required-ness is a separate concern.
     *
     * Note that a hidden-but-enabled control still carries its last value into this rule; clear
     * that value when the field leaves the UI, or the error outlives the field showing it.
     *
     * By default the error is returned to the group. Pass `target` to push it down to the start
     * control, the end control, or both, so the field's own form-field renders the message —
     * the validator then returns `null`, and the group is invalid through its child instead.
     * A control that already carries an error of its own keeps it; the rule never overwrites
     * `required` and friends, and only ever clears the error it wrote itself.
     *
     * @static
     * @param options - Control names, the rule to enforce, and the message to show. See {@link IDateCompareOptions}
     * @returns A `ValidatorFn` to attach at the `FormGroup` level
     * @example
     * ```typescript
     * this._fb.group({
     *   start_time: this._fb.control<string | null>(null),
     *   end_time:   this._fb.control<string | null>(null),
     * }, {
     *   validators: [DateValidator.compare({
     *     startKey: 'start_time',
     *     endKey:   'end_time',
     *     operator: ComparisonOperator.LessThan,
     *   })],
     * });
     * ```
     */
    public static compare(options: IDateCompareOptions): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // ES6 destructuring
            const {
                startKey,
                endKey,
                operator = ComparisonOperator.LessThanOrEqual,
                i18nKey,
                i18nParams,
                errorName = 'compare',
                target = CompareErrorTarget.Group,
            } = options;

            const { [startKey]: startControl, [endKey]: endControl } = (control as FormGroup).controls ?? {};

            // `null` while the rule holds, is void, or the two sides are not comparable at all
            const errors = DateValidator._violates(startControl, endControl, operator)
                ? new AppValidationErrorsModel(errorName, { startKey, endKey, operator }, {
                    key: i18nKey ?? COMPARE_I18N_KEYS[operator as ComparisonOperatorType],
                    params: i18nParams,
                }).errors
                : null;

            // group-level target — hand the error back the plain Angular way
            if (target === CompareErrorTarget.Group) return errors;

            // control-level target — the rule still needs the group to see both sides, but the
            // message belongs next to the field, so it is pushed down to the sibling control(s)
            if (DateValidator._targets(target, CompareErrorTarget.Start)) {
                DateValidator._publish(startControl, errors, errorName);
            }

            if (DateValidator._targets(target, CompareErrorTarget.End)) {
                DateValidator._publish(endControl, errors, errorName);
            }

            // the group itself stays error-free — it is invalid anyway while a child is
            return null;
        };
    }

    // ─── private helpers ─────────────────────────────────────────────────────

    /**
     * @internal Whether the configured target covers one specific side.
     *
     * @param target - The configured target
     * @param side - The side being asked about
     * @returns `true` for an exact match and for {@link CompareErrorTarget.Both}
     */
    private static _targets(target: ICompareErrorTarget, side: CompareErrorTarget): boolean {
        return target === side || target === CompareErrorTarget.Both;
    }

    /**
     * @internal Whether the rule is violated for the given pair of controls.
     *
     * Void (`false`) whenever a side is missing, disabled or empty — a disabled side is excluded
     * from the group's own value, and an empty one is `required`'s concern, not this validator's.
     *
     * @param startControl - Control holding the left side of the comparison
     * @param endControl - Control holding the right side of the comparison
     * @param operator - The rule to evaluate, read start-first
     * @returns `true` only when both sides are comparable and the rule does not hold
     */
    private static _violates(startControl: AbstractControl | undefined, endControl: AbstractControl | undefined, operator: IComparisonOperator): boolean {

        if (startControl?.disabled || endControl?.disabled) return false;

        const startValue = startControl?.value;
        const endValue = endControl?.value;

        if (DateValidator._isEmpty(startValue) || DateValidator._isEmpty(endValue)) return false;

        return DateValidator._satisfies(startValue, endValue, operator) === false;
    }

    /**
     * @internal Pushes — or withdraws — this rule's error on a sibling control.
     *
     * The control's own validators always win: an error already sitting on the control is never
     * overwritten, so `required` and friends keep showing their message and this rule simply
     * stays on the group. Conversely only what this rule wrote is ever cleared — `errorName`
     * doubles as the ownership marker, since errors carry a flat
     * (`errorName` / `errorValue` / `i18n`) shape.
     *
     * `setErrors` recalculates ancestor status without re-running their validators, so calling it
     * from inside a group validator does not recurse. It is emitted silently — the group's own
     * `updateValueAndValidity` pass, the one that invoked this validator, emits right after.
     *
     * @param control - The sibling control the message belongs to, when it exists
     * @param errors - The error to publish, or `null` to withdraw a previously published one
     * @param errorName - Stable identifier of this rule's error
     */
    private static _publish(control: AbstractControl | undefined, errors: ValidationErrors | null, errorName: string): void {

        if (control == null) return;

        const currentErrors = control.errors;
        const owned = currentErrors?.['errorName'] === errorName;

        if (errors != null) {

            // the control speaks for itself — either it already shows this error, or it shows one
            // of its own that must not be overwritten
            if (currentErrors != null) return;

            control.setErrors(errors, { emitEvent: false });

            return;
        }

        // rule holds — withdraw only what this rule wrote
        if (owned) control.setErrors(null, { emitEvent: false });
    }

    /**
     * @internal Whether a side of the comparison carries no value to compare.
     *
     * @param value - The raw control value
     * @returns `true` for `null`, `undefined` and the empty string
     */
    private static _isEmpty(value: unknown): boolean {
        return value == null || (typeof value === 'string' && value.trim().length === 0);
    }

    /**
     * @internal Evaluates the rule for one pair of raw control values.
     *
     * Both sides are first resolved to timestamps; when either side is a string that carries no
     * parseable date — `HH:mm` times, most notably — the pair falls back to a plain string
     * compare, which is correct for the zero-padded `YYYY-MM-DD` / `HH:mm` formats the app emits.
     *
     * @param startValue - Raw value of the start control
     * @param endValue - Raw value of the end control
     * @param operator - The rule to evaluate, read start-first
     * @returns `true` when the rule holds, `false` when it is violated, `null` when the two
     *          values cannot be compared at all (mixed, unparseable input)
     */
    private static _satisfies(startValue: unknown, endValue: unknown, operator: IComparisonOperator): boolean | null {

        const startTimestamp = DateValidator._timestamp(startValue);
        const endTimestamp = DateValidator._timestamp(endValue);

        // both sides resolved to an instant — compare on the timeline
        if (startTimestamp !== null && endTimestamp !== null) {
            return DateValidator._evaluate(startTimestamp, endTimestamp, operator);
        }

        // neither side is a date — fall back to a lexicographic compare (e.g. `HH:mm`)
        if (startTimestamp === null && endTimestamp === null && typeof startValue === 'string' && typeof endValue === 'string') {
            return DateValidator._evaluate(startValue.trim(), endValue.trim(), operator);
        }

        // one side is a date and the other is not — nothing meaningful to compare
        return null;
    }

    /**
     * @internal Resolves a raw control value to a timestamp.
     *
     * @param value - `Date`, epoch milliseconds, or a date/datetime string
     * @returns The epoch milliseconds, or `null` when the value carries no parseable date
     */
    private static _timestamp(value: unknown): number | null {

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value.getTime();
        }

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (typeof value === 'string') {
            const parsed = Date.parse(value.trim());
            return Number.isNaN(parsed) ? null : parsed;
        }

        return null;
    }

    /**
     * @internal Applies the operator to an already-normalised, same-typed pair.
     *
     * @param start - Left side of the comparison
     * @param end - Right side of the comparison
     * @param operator - The rule to evaluate
     * @returns `true` when the rule holds
     */
    private static _evaluate<TValue extends number | string>(start: TValue, end: TValue, operator: IComparisonOperator): boolean {
        switch (operator) {
            case ComparisonOperator.LessThan:
                return start < end;
            case ComparisonOperator.LessThanOrEqual:
                return start <= end;
            case ComparisonOperator.GreaterThan:
                return start > end;
            case ComparisonOperator.GreaterThanOrEqual:
                return start >= end;
            case ComparisonOperator.Equal:
                return start === end;
            case ComparisonOperator.NotEqual:
                return start !== end;
            default:
                return true;
        }
    }

}
